import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import SendToMobileRoundedIcon from "@mui/icons-material/SendToMobileRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import { selectUser } from "../../../features/userSlice";
import { Card, PageHeader, PageShell, SectionHeader } from "../../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../../components/UI/States";
import { Notice, Pill, SearchField, Segmented } from "../../../components/Fleet/FleetParts";
import { formatDate, formatDateTime, TONE_CHIP } from "../../../components/Fleet/fleetFormat";
import {
  CHANNEL_LABEL,
  describeRecipient,
  fetchAppCodes,
  fillTemplate,
  importAppCodes,
  saveAppCodeTemplates,
  sendAppCode,
  smsParts,
} from "./appCodesApi";
import { readCodeSheet } from "./codeSheet";

// Hands out Node Mobile download codes (Apple Business Manager custom app
// codes) by text (ClickSend) or email (noreply@). Admin / IT only. Scripts can do
// the same through /api/appcodes/send?to=…&key=… (see the Script API tab).

const LOW_STOCK = 25;

const TABS = [
  { value: "activity", label: "Activity" },
  { value: "message", label: "Message" },
  { value: "import", label: "Import codes" },
  { value: "api", label: "Script API" },
];

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const SuccessNotice = ({ children }) => (
  <p
    role="status"
    className={`mt-4 flex items-start gap-2 rounded-xl px-3 py-2 text-sm ${TONE_CHIP.emerald}`}
  >
    <CheckCircleRoundedIcon sx={{ fontSize: 18 }} className="mt-px shrink-0" />
    <span>{children}</span>
  </p>
);

const ChannelIcon = ({ channel, className = "" }) => {
  const Icon = channel === "email" ? MailOutlineRoundedIcon : SmsRoundedIcon;
  return <Icon fontSize="small" className={className} />;
};

// ------------------------------------------------------------------ send

const SendCard = ({ userId, data, onSent, onImportClick }) => {
  const [to, setTo] = useState("");
  const [fresh, setFresh] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const hint = describeRecipient(to);
  // Texting needs ClickSend; email goes out from noreply@ either way.
  const textingOff = hint?.channel === "sms" && !data.config.clicksend;
  const canSend = hint && !hint.error && !textingOff && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await sendAppCode(userId, to, { fresh }));
      setTo("");
      setFresh(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      onSent();
    }
  };

  if (data.counts.total === 0) {
    return (
      <Card className="mb-6">
        <EmptyState
          compact
          icon={UploadFileRoundedIcon}
          title="No codes yet."
          description="Import the code spreadsheet from Apple Business Manager, then you can text or email codes from here."
          action={
            <button type="button" className="app-btn app-btn-primary" onClick={onImportClick}>
              Import codes
            </button>
          }
        />
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <SectionHeader
        title="Send a code"
        description="Type a phone number to text it, or an email address to email it. The next unused code goes out automatically."
      />
      <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <TextField
          size="small"
          label="Phone number or email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          autoComplete="off"
          className="w-full sm:max-w-md"
          helperText={
            textingOff
              ? "Texting is off — enter an email address instead"
              : hint
              ? hint.error ||
                `We'll ${hint.channel === "sms" ? "text" : "email"} ${hint.display}`
              : " "
          }
          error={textingOff || Boolean(to.trim() && hint?.error && to.trim().length > 6)}
        />
        <button type="submit" className="app-btn app-btn-primary sm:mt-0.5" disabled={!canSend}>
          {hint?.channel && <ChannelIcon channel={hint.channel} />}
          {busy ? "Sending…" : "Send code"}
        </button>
      </form>
      <FormControlLabel
        className="mt-1"
        control={
          <Checkbox size="small" checked={fresh} onChange={(e) => setFresh(e.target.checked)} />
        }
        label={
          <span className="text-sm">
            Send a new code even if they already got one
            <span className="app-muted block text-xs">
              Otherwise someone who already has a code gets the same one again — they only need one.
            </span>
          </span>
        }
      />

      {error && (
        <div className="mt-4">
          <Notice tone="rose">{error}</Notice>
        </div>
      )}
      {result && (
        <SuccessNotice>
          {result.reused ? (
            <>
              Re-sent the code {result.display} already had (
              <span className="font-mono font-semibold">{result.code}</span>, first sent{" "}
              {formatDate(result.firstSentAt)}). If someone else used it, check “Send a new code” and
              send again.
            </>
          ) : (
            <>
              {result.channel === "sms" ? "Texted" : "Emailed"} code{" "}
              <span className="font-mono font-semibold">{result.code}</span> to {result.display}.{" "}
              {plural(result.remaining, "code")} left.
            </>
          )}{" "}
          Activity shows when they open the link.
          {result.reminder && " If they haven't opened it or signed in to Node Mobile in 2 days, they'll get one reminder email."}
        </SuccessNotice>
      )}
    </Card>
  );
};

// ------------------------------------------------------------- activity

const sendStatus = (s) =>
  !s.ok
    ? { label: s.reminder ? "Reminder failed" : "Failed", tone: "rose" }
    : s.reminder
    ? { label: "Reminder", tone: "amber" }
    : s.reused
    ? { label: "Re-sent", tone: "sky" }
    : { label: "Sent", tone: "emerald" };

// Whether the person tapped the tracked link in that text or email. Sends
// from before tracked links (no token) and failed sends have no open state.
const OPEN_FILTERS = {
  all: () => true,
  opened: (s) => s.clicks > 0,
  unopened: (s) => s.ok && s.tracked && s.clicks === 0,
};

const ActivityPanel = ({ sends }) => {
  const [query, setQuery] = useState("");
  const [opened, setOpened] = useState("all");
  const q = query.trim().toLowerCase();
  const searched = q
    ? sends.filter((s) =>
        [s.display, s.to, s.code, s.byName].some((v) => String(v || "").toLowerCase().includes(q))
      )
    : sends;
  const visible = searched.filter(OPEN_FILTERS[opened]);
  const filterOptions = [
    { value: "all", label: "All" },
    { value: "opened", label: "Opened", count: searched.filter(OPEN_FILTERS.opened).length },
    { value: "unopened", label: "Not opened", count: searched.filter(OPEN_FILTERS.unopened).length },
  ];

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchField value={query} onChange={setQuery} placeholder="Search phone, email, code, or person…" />
        <Segmented options={filterOptions} value={opened} onChange={setOpened} label="Filter by opened" />
        <span className="app-muted text-sm tabular-nums sm:ml-auto">{plural(visible.length, "send")}</span>
      </div>
      <Card padded={false} className="overflow-hidden">
        {visible.length === 0 ? (
          <EmptyState
            compact
            icon={SendToMobileRoundedIcon}
            title={sends.length ? "Nothing matches that." : "No codes sent yet."}
            description="Every code sent from this page or the script API shows up here."
          />
        ) : (
          <ul className="divide-y divide-[color:var(--app-divider)]">
            {visible.map((s) => {
              const status = sendStatus(s);
              return (
                <li key={s.id} className="flex items-start gap-3 px-4 py-3 sm:px-5">
                  <ChannelIcon channel={s.channel} className="app-muted mt-0.5 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{s.display || s.to}</span>
                      <Pill tone={status.tone}>{status.label}</Pill>
                      {s.clicks > 0 ? (
                        <Pill tone="violet" icon={TouchAppRoundedIcon}>
                          Opened
                        </Pill>
                      ) : (
                        OPEN_FILTERS.unopened(s) && <Pill tone="slate">Not opened</Pill>
                      )}
                      {/* A failed send's code went back in the pool (unless it was a re-send). */}
                      {s.code && (s.ok || s.reused) && (
                        <span className="app-muted font-mono text-xs">{s.code}</span>
                      )}
                    </span>
                    {s.error && <span className="block text-sm text-rose-600 dark:text-rose-300">{s.error}</span>}
                    <span className="app-muted block text-xs">
                      {CHANNEL_LABEL[s.channel]} · {s.via === "api" ? "Script API" : s.byName || "Someone"} ·{" "}
                      {formatDateTime(s.at)}
                    </span>
                    {s.clicks > 0 && (
                      <span className="block text-xs text-violet-700 dark:text-violet-300">
                        Opened {formatDateTime(s.firstClickedAt)}
                        {s.clicks > 1 &&
                          ` · ${plural(s.clicks, "tap")}, last ${formatDateTime(s.lastClickedAt)}`}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      <p className="app-muted mt-3 text-xs">
        Shows the latest 300 sends. Opened means someone tapped the link in that text or email — link
        previews and mail scanners that identify themselves aren't counted, though some scanners can still
        look like a tap. Sends from before link tracking show neither.
      </p>
    </>
  );
};

// -------------------------------------------------------------- message

const TEMPLATE_FIELDS = ["sms", "emailSubject", "emailMessage", "reminders", "reminderSubject", "reminderMessage"];

const MessagePanel = ({ userId, templates, defaults, reminderJob, onSaved }) => {
  const [draft, setDraft] = useState(templates);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Only reset the draft when the saved message actually changes — the page
  // reloads after every send, which shouldn't wipe edits in progress.
  const savedKey = JSON.stringify(templates);
  useEffect(() => setDraft(JSON.parse(savedKey)), [savedKey]);

  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setDraft((d) => ({ ...d, [field]: value }));
    setSaved(false);
  };
  const dirty = TEMPLATE_FIELDS.some((f) => draft[f] !== templates[f]);
  const isDefault = TEMPLATE_FIELDS.every((f) => draft[f] === defaults[f]);
  const sms = smsParts(fillTemplate(draft.sms));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await saveAppCodeTemplates(userId, draft);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <SectionHeader
        title="Message"
        description="What people get with their code. {link} becomes their download link — a Node link that forwards to the App Store, so Activity can show who opened it — and {code} the code itself."
      />
      {error && <Notice tone="rose">{error}</Notice>}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <p className="app-eyebrow mb-2">Text message</p>
          <TextField
            multiline
            minRows={4}
            fullWidth
            value={draft.sms}
            onChange={set("sms")}
            helperText={`${sms.length} characters with a real link · about ${plural(sms.parts, "text")}${
              sms.unicode ? " (emoji or special characters make texts shorter)" : ""
            }`}
          />
          <p className="app-eyebrow mb-1 mt-4">Preview</p>
          <div className="app-surface whitespace-pre-wrap break-words rounded-xl p-3 text-sm">
            {fillTemplate(draft.sms)}
          </div>
        </div>

        <div className="min-w-0">
          <p className="app-eyebrow mb-2">Email</p>
          <TextField size="small" fullWidth label="Subject" value={draft.emailSubject} onChange={set("emailSubject")} />
          <TextField
            multiline
            minRows={5}
            fullWidth
            label="Message"
            className="!mt-3"
            value={draft.emailMessage}
            onChange={set("emailMessage")}
            helperText="Leave a blank line between paragraphs. The email adds a Get Node Mobile button and the code under your message."
          />
        </div>
      </div>

      <div className="app-divider mt-6 border-t pt-5">
        <p className="app-eyebrow mb-1">Reminder email</p>
        <FormControlLabel
          control={<Checkbox size="small" checked={draft.reminders} onChange={set("reminders")} />}
          label={
            <span className="text-sm">
              Email one reminder after 2 days if the code looks unused
              <span className="app-muted block text-xs">
                Only for codes sent by email. “Unused” means nobody opened the link and the person hasn't signed in to
                Node Mobile. Reminders go out on weekdays, 8 AM–5 PM Eastern, and show in Activity.
              </span>
            </span>
          }
        />
        {!reminderJob && (
          <div className="mt-2">
            <Notice tone="amber">
              Reminders are off on this server (APP_CODE_REMINDERS=off in the server's .env), so none go out from here.
            </Notice>
          </div>
        )}
        {draft.reminders && (
          <div className="mt-3 grid gap-6 lg:grid-cols-2">
            <div className="min-w-0">
              <TextField size="small" fullWidth label="Reminder subject" value={draft.reminderSubject} onChange={set("reminderSubject")} />
              <TextField
                multiline
                minRows={4}
                fullWidth
                label="Reminder message"
                className="!mt-3"
                value={draft.reminderMessage}
                onChange={set("reminderMessage")}
                helperText="Same layout as the first email: your message, then the Get Node Mobile button and the code."
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button type="button" className="app-btn app-btn-primary" onClick={save} disabled={!dirty || busy}>
          {busy ? "Saving…" : "Save message"}
        </button>
        {dirty && (
          <button type="button" className="app-btn app-btn-ghost" onClick={() => setDraft(templates)}>
            Discard changes
          </button>
        )}
        {!isDefault && (
          <button
            type="button"
            className="app-btn app-btn-ghost app-muted"
            onClick={() => {
              setDraft(defaults);
              setSaved(false);
            }}
          >
            Start from the default
          </button>
        )}
        {saved && !dirty && <span className="text-sm text-emerald-600 dark:text-emerald-300">Saved.</span>}
      </div>
    </Card>
  );
};

// --------------------------------------------------------------- import

const ImportPanel = ({ userId, batches, onImported }) => {
  const inputRef = useRef(null);
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setResult(null);
    setSheet(null);
    try {
      const parsed = await readCodeSheet(file);
      if (!parsed.codes.length) {
        setError(`No app codes found in ${file.name}. Use the code spreadsheet Apple Business Manager gives you.`);
        return;
      }
      setSheet({ ...parsed, fileName: file.name });
    } catch (err) {
      setError(`Couldn't read ${file.name}: ${err.message}`);
    }
  };

  const doImport = async () => {
    setBusy(true);
    setError(null);
    try {
      const { codes, orderId, product } = sheet;
      setResult(await importAppCodes(userId, { codes, orderId, product }));
      setSheet(null);
      onImported();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card className="mb-6">
        <SectionHeader
          title="Import codes"
          description="Upload the code spreadsheet Apple Business Manager gives you when you buy Node Mobile codes (.xls, .xlsx, or .csv). Codes that are already here are skipped, so importing the same file twice is safe."
        />
        {error && <Notice tone="rose">{error}</Notice>}
        <input
          ref={inputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          className="hidden"
          onChange={pick}
        />
        {sheet ? (
          <div className="app-surface flex flex-col gap-3 rounded-xl p-4 sm:flex-row sm:items-center">
            <span className="min-w-0 flex-1 text-sm">
              <span className="block font-semibold">{sheet.fileName}</span>
              <span className="app-muted block">
                {plural(sheet.codes.length, "code")}
                {sheet.orderId ? ` · Order ${sheet.orderId}` : ""}
                {sheet.product ? ` · ${sheet.product}` : ""}
              </span>
            </span>
            <div className="flex gap-2">
              <button type="button" className="app-btn app-btn-ghost" onClick={() => setSheet(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="app-btn app-btn-primary" onClick={doImport} disabled={busy}>
                {busy ? "Importing…" : `Import ${plural(sheet.codes.length, "code")}`}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="app-btn app-btn-secondary" onClick={() => inputRef.current?.click()}>
            <UploadFileRoundedIcon fontSize="small" />
            Choose spreadsheet…
          </button>
        )}
        {result && (
          <SuccessNotice>
            Added {plural(result.added, "new code")}.
            {result.skipped > 0 &&
              ` ${result.skipped === 1 ? "1 code was" : `${result.skipped} codes were`} already here.`}
          </SuccessNotice>
        )}
      </Card>

      {batches.length > 0 && (
        <Card padded={false} className="overflow-x-auto">
          <table className="app-table w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Order</th>
                <th className="text-left">Product</th>
                <th className="text-left">Imported</th>
                <th className="text-right">Codes</th>
                <th className="text-right">Left</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.orderId || "none"}>
                  <td className="font-mono">{b.orderId || "—"}</td>
                  <td>{b.product || "—"}</td>
                  <td>{formatDate(b.importedAt)}</td>
                  <td className="text-right tabular-nums">{b.total}</td>
                  <td className="text-right tabular-nums">{b.available}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
};

// ------------------------------------------------------------ script API

const ApiPanel = ({ configured }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/api/appcodes/send?to=5742185051&key=YOUR_KEY`;

  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Card>
      <SectionHeader
        title="Script API"
        description="Send a code from a script or another system with one request — no sign-in needed, just the key."
      />
      {!configured && (
        <Notice tone="amber">
          The script API is off until APP_CODES_API_KEY is set in the server's .env (and the server restarted).
        </Notice>
      )}
      <div className="app-surface flex items-start gap-2 rounded-xl p-3">
        <code className="min-w-0 flex-1 break-all font-mono text-sm">GET {url}</code>
        <button type="button" className="app-icon-btn shrink-0" onClick={copy} aria-label="Copy URL" title="Copy URL">
          {copied ? <CheckCircleRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
        </button>
      </div>
      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-[8rem_1fr]">
        <dt className="font-mono font-semibold">to</dt>
        <dd className="app-muted">
          A phone number (10 digits for the US, or + and the country code) to text, or an email address to email.
        </dd>
        <dt className="font-mono font-semibold">key</dt>
        <dd className="app-muted">The APP_CODES_API_KEY value from the server's .env.</dd>
        <dt className="font-mono font-semibold">fresh=1</dt>
        <dd className="app-muted">
          Optional. Send a new code even if this person already got one (otherwise they get the same code again).
        </dd>
      </dl>
      <p className="app-muted mt-4 text-sm">
        POST works too, with the same fields in the query string or a JSON body. A successful send answers{" "}
        <code className="font-mono">{`{ "ok": true, "code": "…", "channel": "sms", "reused": false, "remaining": 254 }`}</code>
        . Errors answer <code className="font-mono">{`{ "ok": false, "error": "…" }`}</code> with status 400 (bad
        phone or email), 401 (wrong key), 409 (no codes left), 502 (the text or email didn't go out), or 503 (texting or the key isn't set up).
        Script sends show in Activity as “Script API”, and their links are tracked the same way, so they show as
        opened or not.
      </p>
    </Card>
  );
};

// ----------------------------------------------------------------- page

const AppCodes = () => {
  const user = useSelector(selectUser);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("activity");

  const load = useCallback(() => {
    if (!user?.id) return;
    fetchAppCodes(user.id)
      .then((next) => {
        setData(next);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, [user?.id]);

  useEffect(() => load(), [load]);

  const counts = data?.counts;

  return (
    <PageShell size="lg">
      <PageHeader
        icon={SendToMobileRoundedIcon}
        tone="sky"
        eyebrow="Admin"
        eyebrowTo="/admin"
        title="Mobile App Codes"
        description="Text or email someone a download code for Node Mobile, the Big C iPhone app. Each code installs the app once."
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {!data && !error && <LoadingState label="Loading app codes…" />}

      {data && (
        <>
          {!data.config.clicksend && (
            <Notice tone="amber">
              Texting is off: the server has no ClickSend credentials (CLICK_API in the server's .env).
              Emailing codes still works.
            </Notice>
          )}
          {counts.total > 0 && counts.available < LOW_STOCK && (
            <Notice tone="amber">
              {counts.available === 0 ? "No codes left." : `Only ${plural(counts.available, "code")} left.`} Buy more
              in Apple Business Manager and import the spreadsheet on the Import codes tab.
            </Notice>
          )}

          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              label="Codes left"
              value={counts.available}
              tone={counts.available < LOW_STOCK ? "amber" : "emerald"}
            />
            <StatTile label="Sent" value={counts.sent} />
            <StatTile label="Opened" value={counts.opened} />
            <StatTile label="Imported" value={counts.total} />
          </div>

          <SendCard userId={user?.id} data={data} onSent={load} onImportClick={() => setTab("import")} />

          <div className="mb-4">
            <Segmented options={TABS} value={tab} onChange={setTab} label="App code sections" />
          </div>

          {tab === "activity" && <ActivityPanel sends={data.sends} />}
          {tab === "message" && (
            <MessagePanel
              userId={user?.id}
              templates={data.templates}
              defaults={data.defaults}
              reminderJob={data.config.reminderJob}
              onSaved={load}
            />
          )}
          {tab === "import" && <ImportPanel userId={user?.id} batches={data.batches} onImported={load} />}
          {tab === "api" && <ApiPanel configured={data.config.apiKey} />}
        </>
      )}
    </PageShell>
  );
};

export default AppCodes;
